import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@sportscanner/ui";
import { clubRulesSchema } from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { type MeClub } from "@/lib/me";
import RichTextEditor from "./rich-text-editor";

interface Props {
  clubId: string;
}

type TabKey = "sr" | "en" | "ru";

function emptyish(html: string): boolean {
  const trimmed = html.trim();
  return trimmed === "" || trimmed === "<p></p>";
}

export default function ClubRulesForm({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabKey>("sr");
  const [rulesSr, setRulesSr] = useState("");
  const [rulesEn, setRulesEn] = useState("");
  const [rulesRu, setRulesRu] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
  });
  const club = clubQuery.data;

  useEffect(() => {
    if (!club) return;
    // Mirror the description pattern: rulesSr is canonical, fall back to the
    // legacy primary `rules` column if older data only set that.
    setRulesSr(club.rulesSr || club.rules || "");
    setRulesEn(club.rulesEn || "");
    setRulesRu(club.rulesRu || "");
    setErrorCode(null);
  }, [club]);

  const isDirty = club
    ? rulesSr !== (club.rulesSr || club.rules || "") ||
      rulesEn !== (club.rulesEn || "") ||
      rulesRu !== (club.rulesRu || "")
    : false;

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = clubRulesSchema.safeParse({
        rulesSr: emptyish(rulesSr) ? null : rulesSr,
        rulesEn: emptyish(rulesEn) ? null : rulesEn,
        rulesRu: emptyish(rulesRu) ? null : rulesRu,
      });
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      await apiPut(`/api/admin/clubs/${clubId}/rules`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("clubRules.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (clubQuery.isError || !club) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
        <TabsList className="w-full">
          <TabsTrigger value="sr">{t("clubRules.tabs.sr")}</TabsTrigger>
          <TabsTrigger value="en">{t("clubRules.tabs.en")}</TabsTrigger>
          <TabsTrigger value="ru">{t("clubRules.tabs.ru")}</TabsTrigger>
        </TabsList>

        <TabsContent value="sr" className="mt-4">
          <RichTextEditor
            value={rulesSr}
            onChange={setRulesSr}
            placeholder={t("clubRules.placeholder")}
            disabled={mutation.isPending}
          />
        </TabsContent>
        <TabsContent value="en" className="mt-4">
          <RichTextEditor
            value={rulesEn}
            onChange={setRulesEn}
            placeholder={t("clubRules.placeholder")}
            disabled={mutation.isPending}
          />
        </TabsContent>
        <TabsContent value="ru" className="mt-4">
          <RichTextEditor
            value={rulesRu}
            onChange={setRulesRu}
            placeholder={t("clubRules.placeholder")}
            disabled={mutation.isPending}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
